import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

import jwtService from '@/services/jwtService'
import emitter from '@/services/emitter'

interface TokenRefreshResponse {
  access_token: string
  expires_in: number
  token_type: string
}

type InternalAxiosRequestConfigWithRetry = InternalAxiosRequestConfig & { _retry?: boolean }

class HttpClient {
  private readonly instance: AxiosInstance
  private isRefreshing = false
  private failedQueue: Array<{
    resolve: (value: unknown) => void
    reject: (reason?: unknown) => void
  }> = []

  /**
   * Конструктор HttpClient
   * @param baseURL - Базовый URL для всех запросов API
   */
  constructor(baseURL: string = '') {
    this.instance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      validateStatus: (status: number) => status >= 200 && status < 500,
    })

    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const language = 'ru-RU' // todo hardcoded value
        if (language) {
          config.headers['X-Language'] = language
        }

        if (!config.url?.includes('/api/connect/token') && !config.headers['Authorization']) {
          const token = jwtService.getToken()
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
          }
        }
        return config
      },
      (error) => Promise.reject(error),
    )

    this.instance.interceptors.response.use(
      async (response: AxiosResponse) => {
        if (response.status === 401) {
          console.warn(
            'Intercepted 401 in onFulfilled handler (due to custom validateStatus). Attempting refresh...',
          )
          try {
            const retryResponse = await this.handleUnauthorizedAttempt(
              response.config as InternalAxiosRequestConfigWithRetry,
            )
            return retryResponse
          } catch (refreshError) {
            return Promise.reject(refreshError)
          }
        }
        return response
      },

      (error: AxiosError) => {
        console.error('onRejected handler called (status >= 500 or network error):', error)
        return Promise.reject(error)
      },
    )
  } // Конец конструктора

  /**
   * Обрабатывает попытку обновления токена и повтора запроса при получении 401.
   * Используется внутри интерсептора ответа.
   * @param originalRequest - Конфигурация оригинального запроса.
   * @returns Промис с ответом от повторного запроса или отклоненный промис.
   */
  private async handleUnauthorizedAttempt(
    originalRequest: InternalAxiosRequestConfigWithRetry,
  ): Promise<AxiosResponse> {
    if (originalRequest._retry) {
      console.warn(
        'handleUnauthorizedAttempt called on a request already marked for retry. Rejecting.',
      )
      throw new Error('Refresh token attempt failed after retry.')
    }

    if (this.isRefreshing) {
      console.log('Another request is already refreshing the token. Queueing request...')
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject })
      }).then(async (token) => {
        console.log('Refreshing finished for queued request. Retrying...')
        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = 'Bearer ' + token
        }
        return await this.instance(originalRequest)
      })
    }

    originalRequest._retry = true
    this.isRefreshing = true

    try {
      console.log('Attempting token refresh...')
      const response = await axios.post<TokenRefreshResponse>(
        `${this.instance.defaults.baseURL || ''}/api/connect/token`,
        new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: 'spa_app',
        }),
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          withCredentials: true,
        },
      )

      const newAccessToken = response.data.access_token
      jwtService.saveToken(newAccessToken)
      console.log('Token refreshed successfully.')

      if (originalRequest.headers) {
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
      }

      this.processQueue(null, newAccessToken)
      this.isRefreshing = false
      console.log('Retrying original request with new token...')
      return await this.instance(originalRequest)
    } catch (refreshError: unknown) {
      console.error('Failed to refresh token:', refreshError)
      jwtService.clear()
      this.processQueue(refreshError, null)
      this.notifyLogoutRequired('refreshFailed')
      this.isRefreshing = false
      throw refreshError
    }
  }

  /**
   * Обрабатывает очередь ожидающих запросов.
   * @param error - Ошибка (если обновление не удалось) или null.
   * @param token - Новый токен (если обновление успешно) или null.
   */
  private processQueue(error: unknown | null, token: string | null = null): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })
    this.failedQueue = []
  }

  /**
   * Генерирует событие 'logoutRequired' через глобальный эмиттер.
   * @param reason - Опциональная причина события.
   */
  private notifyLogoutRequired(reason?: string): void {
    console.log(`Emitting logoutRequired event. Reason: ${reason || 'N/A'}`)
    emitter.emit('logoutRequired', { reason })
  }

  public async get<T = unknown>(
    url: string,
    queryParams?: Record<string, unknown>,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.instance.get<T>(url, {
        ...config,
        params: queryParams,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams()
          for (const key in params) {
            if (Object.prototype.hasOwnProperty.call(params, key)) {
              const value = params[key]
              if (Array.isArray(value)) {
                value.forEach((v) => searchParams.append(key, String(v)))
              } else if (value !== null && value !== undefined) {
                searchParams.append(key, String(value))
              }
            }
          }
          return searchParams.toString()
        },
      })
      return response.data
    } catch (error) {
      console.error(`GET request to ${url} failed:`, error)
      throw error
    }
  }

  public async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.post<T>(url, data, config)
    } catch (error) {
      console.error(`POST request to ${url} failed:`, error)
      throw error
    }
  }

  public async postForm<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.postForm<T>(url, data, config)
    } catch (error) {
      console.error(`POST form request to ${url} failed:`, error)
      throw error
    }
  }

  public async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.put<T>(url, data, config)
    } catch (error) {
      console.error(`PUT request to ${url} failed:`, error)
      throw error
    }
  }

  public async putForm<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.putForm<T>(url, data, config)
    } catch (error) {
      console.error(`PUT form request to ${url} failed:`, error)
      throw error
    }
  }

  public async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.delete<T>(url, config)
    } catch (error) {
      console.error(`DELETE request to ${url} failed:`, error)
      throw error
    }
  }
}

const httpClientInstance = new HttpClient()
export default httpClientInstance
